/**
 * Analytics hook — fire-and-forget event tracking.
 *
 * Usage:
 *   const { trackEvent, trackPageView } = useAnalytics();
 *   trackPageView('/assessment', '/dashboard');
 *   trackEvent('assessment_submitted', { opcoId: '...' });
 *
 * Events are sent to POST /api/analytics/events.
 * Errors are silently swallowed — analytics never interrupts user flows.
 */

import { useCallback, useRef } from 'react';
import { useAuth } from '../auth/AuthContext.jsx';
import { apiClient } from '../api/client.js';

export type AnalyticsMetadata = Record<string, unknown>;

export interface UseAnalyticsResult {
  trackEvent: (eventType: string, metadata?: AnalyticsMetadata) => void;
  trackPageView: (page: string, fromPage?: string | null) => void;
}

/** Get or create a sessionId that persists for the browser session */
function getSessionId(): string {
  let id = sessionStorage.getItem('analytics_session_id');
  if (!id) {
    id =
      crypto.randomUUID?.() ??
      Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem('analytics_session_id', id);
  }
  return id;
}

export function useAnalytics(): UseAnalyticsResult {
  const { user } = useAuth();
  const sessionIdRef = useRef<string>(getSessionId());
  // Keep user in a ref so callbacks always see the latest value
  // without needing to be re-created (which breaks useEffect dependencies)
  const userRef = useRef(user);
  userRef.current = user;

  const trackEvent = useCallback(
    (eventType: string, metadata: AnalyticsMetadata = {}) => {
      const u = userRef.current;
      if (!u?.username) return;

      const payload = {
        eventType,
        userId: u.username,
        sessionId: sessionIdRef.current,
        page: window.location.pathname,
        timestamp: new Date().toISOString(),
        metadata,
      };

      // Fire and forget — never await, never show errors
      apiClient.post('/api/analytics/events', payload).catch(() => {});
    },
    []
  );

  const trackPageView = useCallback(
    (page: string, fromPage?: string | null) => {
      const u = userRef.current;
      if (!u?.username) return;

      const payload = {
        eventType: 'page_view',
        userId: u.username,
        sessionId: sessionIdRef.current,
        page,
        fromPage: fromPage || null,
        timestamp: new Date().toISOString(),
      };

      apiClient.post('/api/analytics/events', payload).catch(() => {});
    },
    []
  );

  return { trackEvent, trackPageView };
}
